/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import { computed } from 'mobx';
import * as React from 'react';

import { Button, IModuleProps, INodeProps, KeyCodes, Modal, ModalBody, Popover } from '@msdyn365-commerce-modules/utilities';
import { CartIconComponent, WishListIconComponent } from '@msdyn365-commerce/components';
import { IRequestContextUser } from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';

import { ILinkGroup } from '../../types/links';
import { NavIcon } from './components';
import { ICustomerAccountData, IFarmlandsHeaderData, IHeaderData } from './farmlands-header.data';
import { IFarmlandsHeaderProps, IFarmlandsHeaderResources, IMyAccountLinksData } from './farmlands-header.props.autogenerated';
import { getUserAccountDetailsAsync } from '../../actions/farmlands-account-details-calls.action';
import { getContactDetailsAsync } from '../../actions/farmlands-get-contact-details-calls.action';
import { isShareholderAccountDisabled, isShareholderAccountNotOnHold, hasEcommerceAccess } from '../../common/method/functions';
import { AccountSwitchModal, RefreshPageModal } from '../../common/components/account-switch-modal';
import { getAccountList } from '../../common/components/multi-account';

export interface IHeaderState {
    mobileMenuCollapsed: boolean;
    signinPopoverOpen: boolean;
    isAccountModalOpen: boolean;
    isRefreshModalOpen: boolean;
    headerData?: any;
    accountData?: any;
    customerAccounts?: ICustomerAccountData[];
    hasEcomPermission: boolean;
}

export interface IHeaderViewProps extends IFarmlandsHeaderProps<IFarmlandsHeaderData>, IHeaderState {
    wishListIconDesktop: React.ReactNode;
    wishListIconMobile: React.ReactNode;
    cartIcon: React.ReactNode;
    isOpen: boolean;
    navIcon: React.ReactNode;
    className: string;
    menuBar: React.ReactNode[];
    search: React.ReactNode[];
    HeaderTag: IModuleProps;
    HeaderContainer: INodeProps;
    MobileMenuContainer: INodeProps;
    MobileMenuBodyContainer: INodeProps;
    MobileMenuLinksContainer: INodeProps;
    AccountSwitchModal: JSX.Element;
    RefreshPageModal: JSX.Element;
    toggleAccountModal: () => void;
    togglePopover: () => void;
    HeaderTopBarContainer: INodeProps;
    HeaderSecondaryNavContainer: INodeProps;

    AccountName?: React.ReactNode;
    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverContentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    accountLinksContainer: INodeProps;
    accountLinks?: React.ReactNode[];
    totalAccountLength: number;
    accountList?: React.ReactNode[];
    topBarNavLinks?: React.ReactNode[];
    topBarNavMobileLinks?: React.ReactNode[];
    contactUsIcon?: React.ReactNode;
    homeLinkAnchor?: React.ReactNode;
}

interface ICustomerAccount {
    AccountNumber?: string;
    AccountStatus?: string;
}
/**
 *
 * Header component
 * @extends {React.PureComponent<IFarmlandsHeaderProps<IFarmlandsHeaderData>>}
 */
class Header extends React.PureComponent<IFarmlandsHeaderProps<IFarmlandsHeaderData>, IHeaderState> {
    private popOverRef: React.RefObject<HTMLButtonElement>;

    @computed public get displayName(): Readonly<string | undefined> {
        const customer = this.props.data.accountInformation?.result;
        return customer ? customer.FirstName || customer.Name : undefined;
    }

    @computed get canAccessCheckout(): boolean {
        const shareholderData = this.props.data.cart.result?.cart.ExtensionProperties!;
        if (this.props.context.request.user.token) {
            if (shareholderData) {
                return !isShareholderAccountDisabled(shareholderData) && isShareholderAccountNotOnHold(shareholderData);
            }
            return false;
        }
        return true;
    }

    constructor(props: IFarmlandsHeaderProps<IFarmlandsHeaderData>) {
        super(props);
        this.popOverRef = React.createRef();
        this._toggleNavbar = this._toggleNavbar.bind(this);
        this._togglePopover = this._togglePopover.bind(this);
        this._keydown = this._keydown.bind(this);
        this.state = {
            mobileMenuCollapsed: true,
            signinPopoverOpen: false,
            hasEcomPermission: false,
            isAccountModalOpen: false,
            isRefreshModalOpen: false
        };
    }
    public async componentDidMount(): Promise<void> {
        // this._silentSignOn();
        window && window.addEventListener('keydown', this._keydown);
        if (this.props.context.request.user.token) {
            const accountData = await getUserAccountDetailsAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
            );
            const userContactDetails = await getContactDetailsAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
            );

            const customerAccounts: ICustomerAccountData[] = userContactDetails.CustomerAccounts;

            const sortedAccounts =
                customerAccounts && customerAccounts?.length > 0
                    ? customerAccounts.sort((a, b) => {
                        if (a?.IsActive) {
                            return -1;
                        } else if (b?.IsActive) {
                            return 1;
                        } else if (a?.CustomerName < b?.CustomerName) {
                            return -1;
                        } else if (a?.CustomerName > b?.CustomerName) {
                            return 1;
                        } else {
                            return 0;
                        }
                    })
                    : [];

            this.setState({
                customerAccounts: sortedAccounts
            });

            this.setState({
                accountData
            });

            if (hasEcommerceAccess(userContactDetails)) {
                this.setState({
                    hasEcomPermission: true
                });
            }

            //Get Customer Account and check customer type for analytics
            const accountNumber = this.state.accountData.CustomerAccountNumber;
            const customerAccount = userContactDetails.CustomerAccounts?.find(
                (obj: ICustomerAccount) => obj?.AccountNumber === accountNumber
            );
            if (customerAccount && customerAccount.Permissions && customerAccount.Permissions.length > 0) {
                if (customerAccount.Permissions[0].AuthorityLevel.includes('Casual')) {
                    (Window as any).digitalData.user.customerType = 'casual';
                }
                else {
                    (Window as any).digitalData.user.customerType = 'shareholder';
                }
            }
        } else {
            this.setState({ hasEcomPermission: true });
            (Window as any).digitalData.user.customerType = 'guest';
        }
        if (sessionStorage.getItem('findingMethod')) {
            (Window as any).digitalData.page.attributes.findingMethod = sessionStorage.getItem('findingMethod');
        }
        this.props.data.headerAction.then((data) => {
            this.setState({
                headerData: data.header
            });
        });

        //ToDo: Change to functional component if data does not get passed correctly consistently. Use react hook with dependency on document.readyState
        //https://stackoverflow.com/questions/24947837/javascript-page-is-fully-loaded-boolean-check
        window.dispatchEvent(new CustomEvent('PageLoad'));
        sessionStorage.removeItem('findingMethod');
        await this._logoutInactiveUser();
    }

    public componentWillUnmount(): void {
        window && window.removeEventListener('keydown', this._keydown, false);
    }

    public render(): JSX.Element | null {
        const {
            id,
            slots,
            typeName,
            data: {
                accountInformation,
                cart: { result: cart }
            },
            context: {
                request: { user }
            },
            context,
            resources: { cartLabel, cartQtyLabel }
        } = this.props;

        const { hasEcomPermission } = this.state;

        const headerClassName = classnames('ms-header', this.props.config.className);
        const navbarKey = 'header-nav-mobile';
        const homeLink = this.props.config.homeLink;
        const truncate = (input: string) => (input.length > 21 ? `${input.substring(0, 21)}​​​​​...` : input);
        const customer = accountInformation && accountInformation.result;
        const switchAccountProps = {
            context: { callerContext: context.actionContext, queryResultSettings: {} }
        };
        const viewProps: IHeaderViewProps = {
            ...(this.props as IFarmlandsHeaderProps<IFarmlandsHeaderData>),
            ...this.state,
            homeLinkAnchor: (
                <a className='ms-header__secondarynav-header' href={homeLink?.linkUrl.destinationUrl}>
                    <span>Shop online</span>
                </a>
            ),
            wishListIconDesktop: this.canAccessCheckout && hasEcomPermission && (
                <WishListIconComponent
                    className='ms-header__wishlist-desktop'
                    showButtonTooltip={false}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    data={{}}
                />
            ),
            wishListIconMobile: this.canAccessCheckout && hasEcomPermission && (
                <WishListIconComponent
                    className='ms-header__wishlist-mobile'
                    showButtonTooltip={false}
                    wishlistTooltipText={this.props.resources.wishlistTooltipText}
                    context={context}
                    id={id}
                    typeName={typeName}
                    data={{}}
                />
            ),
            cartIcon:
                this.canAccessCheckout && hasEcomPermission && slots && slots.cartIcon && slots.cartIcon.length > 0
                    ? slots.cartIcon[0]
                    : this.canAccessCheckout &&
                    hasEcomPermission &&
                    cart && (
                        <CartIconComponent
                            cartLabel={cartLabel}
                            cartQtyLabel={cartQtyLabel}
                            context={context}
                            id={id}
                            typeName={typeName}
                            data={{ cart: cart }}
                        />
                    ),
            isOpen: !this.state.mobileMenuCollapsed,
            navIcon: (
                <NavIcon
                    {...{
                        resources: this.props.resources,
                        isExpanded: !this.state.mobileMenuCollapsed,
                        targetId: navbarKey,
                        toggleNavBar: this._toggleNavbar
                    }}
                />
            ),
            menuBar: (slots && slots.menuBar && slots.menuBar.length && slots.menuBar) || [],
            search: (slots && slots.search && slots.search.length && slots.search) || [],
            className: headerClassName,
            HeaderTag: {
                moduleProps: this.props,
                className: classnames(headerClassName),
                tag: 'header'
            },
            HeaderContainer: {
                className: classnames('ms-header__container')
            },
            HeaderTopBarContainer: {
                className: classnames('ms-header__topbar')
            },
            HeaderSecondaryNavContainer: {
                className: classnames('ms-header__secondarynav')
            },
            MobileMenuContainer: {
                tag: Modal,
                id: navbarKey,
                className: 'ms-header__mobile-hamburger',
                isOpen: !this.state.mobileMenuCollapsed,
                wrapClassName: 'ms-header__modal'
            },
            MobileMenuBodyContainer: {
                tag: ModalBody,
                className: 'ms-header__mobile-hamburger-menu-body'
            },
            MobileMenuLinksContainer: {
                className: 'ms-header__mobile-hamburger-menu-links'
            },
            AccountSwitchModal: AccountSwitchModal({
                customerAccounts: this.state.customerAccounts,
                toggleAccountModal: this._toggleAccountModal,
                isAccountModalOpen: this.state.isAccountModalOpen,
                switchAccountProps,
                toggleRefreshModal: this._toggleRefreshPageModal
            }),
            RefreshPageModal: RefreshPageModal({
                isModalOpen: this.state.isRefreshModalOpen,
                toggleRefreshModal: this._toggleRefreshPageModal
            }),
            toggleAccountModal: this._toggleAccountModal,
            AccountInfoDropdownParentContainer: { className: 'ms-header__account-info' },
            AccountName: (
                <span className='ms-header__account-name'>
                    {this.state.accountData?.CustomerName && truncate(this.state.accountData.CustomerName)}
                </span>
            ),
            AccountInfoDropdownPopoverContentContainer:
                (customer &&
                    this.displayName && {
                    tag: Popover,
                    id: 'myprofilePopover',
                    className: 'ms-header__account-info-content',
                    placement: 'bottom',
                    isOpen: this.state.signinPopoverOpen,
                    target: this.popOverRef,
                    toggle: this._togglePopover
                }) ||
                undefined,
            signInLink: this._getSigninButton(customer, user, this.props.resources),
            signOutLink: this._getSignOutButton(customer, user, this.props.resources),
            accountInfoDropdownButton: this._getAccountInfoDropdownButton(customer),
            totalAccountLength: this.state.customerAccounts?.length || 0,
            accountLinksContainer: {
                className: 'ms-header__account-links-container'
            },
            accountLinks: this._getAccountLinks(customer),
            accountList: getAccountList(
                customer,
                switchAccountProps,
                this._toggleRefreshPageModal,
                this.state.customerAccounts,
                this.displayName
            ),
            topBarNavLinks: this._getTopBarNavLinks(this.state.headerData),
            topBarNavMobileLinks: this._getTopBarNavLinks(this.state.headerData, true),
            contactUsIcon: this._getContactUsIcon(this.state.headerData),
            togglePopover: this._togglePopover
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getSigninButton(
        customer: Customer | undefined,
        user: IRequestContextUser | undefined,
        resources: IFarmlandsHeaderResources
    ): React.ReactNode | undefined {
        return (
            !customer && (
                <a
                    className='ms-header__signin-button'
                    title={resources.signInLinkText}
                    href={user && user.signInUrl}
                    aria-label={resources.signInLinkAriaText}
                >
                    <span className='ms-header__signin-button-text' aria-hidden={true}>
                        {resources.signInLinkText}
                    </span>
                </a>
            )
        );
    }

    private _getSignOutButton(
        customer: Customer | undefined,
        user: IRequestContextUser | undefined,
        resources: IFarmlandsHeaderResources
    ): React.ReactNode | undefined {
        return (
            customer &&
            this.displayName && (
                <>
                    <div className='ms-header__flex-break' />
                    <Button
                        onClick={() => this._handleSignOut()}
                        className={'ms-header__signout-button'}
                        title={resources.signOutLinkText}
                        href={(user && user.signOutUrl) || ''}
                        aria-label={resources.signOutLinkAriaText}
                    >
                        <span className='ms-header__signout-button-text' aria-hidden={true}>
                            {resources.signOutLinkText}
                        </span>
                        <div>
                            <i className='material-icons ms-header__login-icon'>login</i>
                        </div>
                    </Button>
                </>
            )
        );
    }

    private _handleSignOut(): void {
        return window.sessionStorage.removeItem('phone');
    }

    private _getAccountLinks(customer: Customer | undefined): React.ReactNode[] | undefined {
        const { myAccountLinks } = this.props.config;

        return customer && this.displayName && myAccountLinks && myAccountLinks.length
            ? myAccountLinks.map((cta: IMyAccountLinksData, index: number) => (
                <Button
                    className='ms-signin-info__account-link-button'
                    key={index}
                    href={cta.linkUrl.destinationUrl}
                    color='link'
                    aria-label={cta.ariaLabel || ''}
                    target={cta.openInNewTab ? '_blank' : undefined}
                >
                    {cta.linkText || ''}
                </Button>
            ))
            : undefined;
    }

    private _getAccountInfoDropdownButton(customer: Customer | undefined): React.ReactNode | undefined {
        return (
            customer &&
            this.displayName && (
                <Button
                    innerRef={this.popOverRef}
                    className={'ms-header__profile-button'}
                    id={'ms-header__account-button'}
                    aria-describedby='myprofilePopover'
                    onClick={this._togglePopover}
                    color='link'
                    aria-expanded={this.state.signinPopoverOpen}
                >
                    <span className='ms-profile-button-text'>{this.displayName.charAt(0)}</span>
                </Button>
            )
        );
    }

    private _getTopBarNavLinks(headerData: IHeaderData | undefined, isMobile: boolean = false): React.ReactNode[] | undefined {
        const choicesRewardsLabel = 'choices rewards';
        // Icon names need to be changed accordingly to represent the nav items
        const mobileNavIconList = ['card_giftcard', 'location_on', 'emoji_events', 'grade'];
        return headerData?.linkGroupList && headerData?.linkGroupList.length
            ? headerData?.linkGroupList.map((linkgroup: ILinkGroup, index: number) =>
                isMobile ? (
                    <div className='ms-header__topbar-nav-mobile-wrapper'>
                        <span className='material-icons-outlined ms-header__topbar-nav-mobile-icon'>{mobileNavIconList[index]}</span>
                        <a
                            className={`ms-header__topbar-nav-mobile-item`}
                            key={index}
                            href={linkgroup.linkList[0].path}
                            // Requirement for only 'Choices Rewards' link to open in new tab.
                            target={linkgroup.linkGroupLabel.toLowerCase() === choicesRewardsLabel ? '_blank' : '_self'}
                        >
                            {linkgroup.linkList[0].label || ''}
                        </a>
                    </div>
                ) : (
                    <a
                        className={`ms-header__topbar-nav-item`}
                        key={index}
                        href={linkgroup.linkList[0].path}
                        // Requirement for only 'Choices Rewards' link to open in new tab.
                        target={linkgroup.linkGroupLabel.toLowerCase() === choicesRewardsLabel ? '_blank' : '_self'}
                    >
                        {linkgroup.linkList[0].label || ''}
                    </a>
                )
            )
            : undefined;
    }

    private _getContactUsIcon(headerData: IHeaderData | undefined): React.ReactNode | undefined {
        return headerData?.contactUsLink ? (
            <div className='ms-header__contactus-container'>
                <a className='ms-header__contactus-icon' href={headerData.contactUsLink}>
                    <img
                        className='ms-header__contactus-icon-img'
                        src='https://files-ap-prod.cms.commerce.dynamics.com/cms/api/qwvspcbgds/binary/MF4RJ'
                        alt='Contact us'
                    />
                </a>
                <span className='ms-header__contactus-tooltip'>Contact us</span>
            </div>
        ) : undefined;
    }

    private _toggleNavbar(): void {
        this.setState({
            mobileMenuCollapsed: !this.state.mobileMenuCollapsed
        });
    }

    private bodyTag = typeof document !== 'undefined' && document.getElementsByTagName('BODY')[0];

    private _togglePopover(): void {
        if (this.bodyTag) {
            const toggled = !this.state.signinPopoverOpen;
            if (toggled) {
                this.bodyTag.classList.add('disable-scroll');
            } else {
                this.bodyTag.classList.remove('disable-scroll');
            }
            this.setState({
                signinPopoverOpen: toggled
            });
        }
    }

    private _toggleAccountModal = (): void => {
        const updatedModalOpen = !this.state.isAccountModalOpen;
        const accountDropdown = document.getElementById('myprofilePopover');
        if (updatedModalOpen) {
            if (accountDropdown) {
                accountDropdown.style.display = 'none';
            }
        } else {
            if (accountDropdown) {
                accountDropdown.style.display = 'unset';
            }
        }
        this.setState({
            isAccountModalOpen: updatedModalOpen
        });
    };

    private _toggleRefreshPageModal = (): void => {
        this.setState({
            isRefreshModalOpen: !this.state.isRefreshModalOpen
        });
    };

    private _keydown(e: KeyboardEvent): void {
        if (e.keyCode === KeyCodes.Escape) {
            this.setState({ mobileMenuCollapsed: true });
        }
    }
    private async _logoutInactiveUser(): Promise<void> {
        if (this.props.context.request.user.token && this.props.context.request.user.signOutUrl) {
            const userContactDetails = await getContactDetailsAsync(
                { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                JSON.parse(atob(this.props.context.request.user.token.split('.')[1])).oid
            );
            if (userContactDetails.CustomerAccounts?.length === 0) {
                window.location.replace(this.props.context.request.user.signOutUrl);
            } else {
                const accountNumber = this.state.accountData.CustomerAccountNumber;
                const customerAccount = userContactDetails.CustomerAccounts?.find(
                    (obj: ICustomerAccount) => obj?.AccountNumber === accountNumber
                );
                if (customerAccount?.AccountStatus === 'Inactive' && customerAccount?.OnHoldStatus !== 'No') {
                    window.location.replace(this.props.context.request.user.signOutUrl);
                }
            }
        }
    }
}

export default Header;
